import React from "react";
import { Link } from "react-router-dom";

// Default assets
import Subscription from "../../../assets/cancel-subscription-image.jpg";
import Ingenuity from "../../../assets/ingenuity-work-image.jpg";
import Tourist from "../../../assets/tourist-image.jpg";
import BoxBackground from "../../../assets/get-demo-box-bg.jpg";
import BoxGrayBackground from "../../../assets/get-demo-box-gray-bg.jpg";

const CrossIcon = () => (
  <svg
    className="pt-0.5 min-w-[21px]"
    width="21"
    height="20"
    viewBox="0 0 21 20"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path d="M20 1.00098L1 19.001" stroke="#FAA42A" strokeWidth="2" />
    <path d="M19.0022 19L2.0022 1" stroke="#FAA42A" strokeWidth="2" />
  </svg>
);

const CheckIcon = () => (
  <svg
    className="min-w-[32px]"
    width="32"
    height="31"
    viewBox="0 0 32 31"
    fill="none"
    xmlns="http://www.w3.org/2000/svg"
  >
    <path
      d="M17.0013 31C16.3345 31 15.6678 31 15.0011 31C14.9311 30.9839 14.8628 30.9572 14.7919 30.9524C12.0159 30.7489 9.45487 29.9195 7.15136 28.4054C3.39358 25.9352 1.08091 22.5307 0.248352 18.1975C0.138344 17.6249 0.0808393 17.0427 0 16.4645C0 15.8185 0 15.1725 0 14.5265C0.0183347 14.4288 0.0466701 14.3319 0.0550041 14.2333C0.306689 11.2633 1.2976 8.53947 3.20524 6.20814C7.37471 1.11184 12.8243 -0.897285 19.4123 0.368108C26.4928 1.72879 31.7632 7.89345 31.9907 14.8826C32.1241 18.9816 30.8231 22.6114 28.0362 25.7034C25.5677 28.4434 22.4592 30.1279 18.7581 30.765C18.1763 30.8651 17.5871 30.9225 17.0013 31ZM26.387 10.2676C26.3736 9.69665 26.0461 9.08374 25.3902 8.64848C24.4568 8.02911 23.2801 8.18092 22.415 9.02963C19.869 11.5289 17.3271 14.0306 14.7828 16.5307C14.1711 17.1324 13.5502 17.1348 12.936 16.5404C11.7867 15.4285 10.6458 14.3076 9.48654 13.2045C8.5623 12.3251 7.17803 12.3243 6.28713 13.1771C5.38623 14.0395 5.36957 15.4018 6.2863 16.299C8.24228 18.2136 10.2083 20.1194 12.1834 22.0147C13.1868 22.9772 14.6453 22.9498 15.6478 21.9799C15.9945 21.6448 16.3295 21.2992 16.6721 20.9608C19.6565 18.0166 22.6392 15.0707 25.6277 12.1305C26.1011 11.6654 26.3911 11.1332 26.387 10.2676Z"
      fill="#FAA42A"
    ></path>
  </svg>
);

const defaultMissionItems = [
  {
    title: "Cancel those Subscriptions",
    description: (
      <ul className="space-y-3 max-w-[617px]">
        <li className="flex items-start gap-4">
          <CrossIcon />
          <span className="text-lg font-light leading-7 text-[#333333]">
            Stop paying for as many as ten subscriptions to ten different
            software services
          </span>
        </li>
        <li className="flex items-start gap-4">
          <CrossIcon />
          <span className="text-lg font-light leading-7 text-[#333333]">
            Stop utilizing frustrating staff and raising costs through
            incompatible and unreliable IT
          </span>
        </li>
        <li className="flex items-start gap-4">
          <CrossIcon />
          <span className="text-lg font-light leading-7 text-[#333333]">
            Stop paying hidden fees and surprise charges
          </span>
        </li>
      </ul>
    ),
    backgroundImage: null,
    image: Subscription,
    buttonText: "Let Laguna Creek show you a better way",
    buttonLink: "/contact-us",
    backgroundColor: "white",
    textColor: "text-[#5FA3B2]",
  },
  {
    title: (
      <div className="flex items-center gap-4">
        <CheckIcon /> Native ingenuity at work
      </div>
    ),
    description: (
      <div>
        <p className="text-lg font-light leading-7 text-[#333333] mb-3">
          Rooted in Indigenous spirit and guided by generations of wisdom, we
          embrace resilience and determination in every challenge we face. Our
          journey is a reflection of strength — turning obstacles into
          opportunities and honoring the traditions that inspire us to move
          forward.
        </p>
        <p className="text-lg font-light leading-7 text-[#333333]">
          With a deep connection to our communities and land, we believe every
          problem has a solution born from creativity and collaboration. By
          blending innovation with cultural values, we pave the way for a future
          where progress and heritage walk hand in hand.
        </p>
      </div>
    ),
    backgroundImage: BoxBackground,
    image: Ingenuity,
    buttonText: "Have Questions? Get In Touch",
    buttonLink: "/contact-us",
    backgroundColor: "white",
    textColor: "text-[#5FA3B2]",
  },
  {
    title: "K'é",
    description: (
      <div>
        <p className="text-lg font-light leading-7 text-white mb-3">
          For the Diné, K'é means solidarity and compassion between all peoples.
          It is the foundation of our lives and our company.
        </p>
        <p className="text-lg font-light leading-7 text-white">
          We donate 10% of all net profits to an annual charitable event on
          Indigenous land.
        </p>
      </div>
    ),
    backgroundImage: BoxGrayBackground,
    image: Tourist,
    buttonText: "Learn More",
    buttonLink: "/about-us",
    backgroundColor: "#B35526",
    textColor: "text-white",
  },
];

const HomeInformationSection = ({ acfData }) => {
  const missionItems = acfData?.our_mission_items?.length
    ? acfData.our_mission_items.map((item, index) => ({
        title: (
          <div
            className="flex items-center gap-4"
            dangerouslySetInnerHTML={{ __html: item.our_mission_item_title }}
          />
        ),
        description: (
          <div
            dangerouslySetInnerHTML={{
              __html: item.our_mission_item_discription,
            }}
          />
        ),
        backgroundImage:
          item.our_mission_item_description_background?.url || null,
        image: item.our_mission_item_image?.url || "",
        buttonText: item.our_mission_item_button_text,
        buttonLink: (() => {
          try {
            return new URL(
              item.our_mission_item_button_link,
              window.location.origin
            ).pathname;
          } catch (e) {
            console.error(
              "Invalid URL in buttonLink:",
              item.our_mission_item_button_link
            );
            return "#";
          }
        })(),
        backgroundColor: index === 2 ? "#B35526" : "white",
        textColor: index === 2 ? "text-white" : "text-[#5FA3B2]",
      }))
    : defaultMissionItems;

  return (
    <section className="py-9">
      <div className="flex flex-col gap-y-8 w-full max-w-[1570px] px-4 mx-auto">
        {missionItems.map((item, index) => (
          <div
            key={index}
            className="flex flex-wrap items-center -mx-4 lg:gap-y-11"
          >
            <div
              className={`w-full max-w-full px-4 ${
                index % 2 === 1 ? "lg:w-2/3 lg:order-2" : "lg:w-1/3"
              }`}
            >
              <div
                className={`flex flex-col gap-y-6 items-start p-8 sm:py-10 sm:px-14 ${
                  item.backgroundColor === "white" ? "bg-white" : "bg-[#B35526]"
                } ${
                  index % 2 === 1
                    ? "lg:max-w-[840px] ml-auto"
                    : "lg:min-w-[781px]"
                } rounded-t-[21px] lg:rounded-[21px] shadow-[0_7px_27px_0px_rgba(0,0,0,0.08)] relative`}
                style={{
                  backgroundImage: item.backgroundImage
                    ? `url(${item.backgroundImage})`
                    : "none",
                  backgroundSize: "cover",
                  backgroundPosition: "center",
                }}
              >
                <h3
                  className={`text-[30px] md:text-[40px] lg:text-[52px] font-normal leading-tight ${item.textColor}`}
                >
                  {item.title}
                </h3>
                <div className="text-lg font-light leading-7 text-[#333333]">
                  {item.description}
                </div>
                <Link
                  to={item.buttonLink}
                  className={`text-lg font-bold leading-6 ${
                    item.textColor
                  } bg-[transparent] border ${
                    item.textColor === "text-white"
                      ? "border-white"
                      : "border-[#5FA3B2]"
                  } py-[17px] px-[17px] md:px-[35px] rounded-xl transition-all ease-in-out duration-500 hover:bg-[#5FA3B2] hover:text-white`}
                >
                  {item.buttonText}
                </Link>
              </div>
            </div>
            <div
              className={`w-full max-w-full px-4 ${
                index % 2 === 1 ? "lg:w-1/3 lg:order-1" : "lg:w-2/3"
              }`}
            >
              <img
                src={item.image}
                alt="OurStory"
                className={`w-full lg:ml-auto rounded-b-[21px] lg:rounded-[21px] ${
                  index % 2 === 1 ? "lg:min-w-[782px]" : "lg:max-w-[840px]"
                }`}
                height="841"
                width="663"
              />
            </div>
          </div>
        ))}
      </div>
    </section>
  );
};

export default HomeInformationSection;
